import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { isIdentityMatch } from '@app/core/misc/identity-matching';
import { toClientTransformer, toClientTypeTransformer } from '@app/core/state/transformers/transformers';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BlaCompany } from 'src/app/core/entity/bla-company';
import { Client } from 'src/app/core/entity/client';
import { ClientBlaCompanyComposite } from 'src/app/core/entity/client-blacompany-composite';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { ClientService } from 'src/app/core/services/clients.service';
import { CloudCompanyService } from 'src/app/core/services/cloud-company.service';
import { ClientActions } from 'src/app/core/state/clients/clients.actions';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { TableColumn } from 'src/app/shared/components/table/interfaces/table-column';
import { translateColumnHeaders } from 'src/app/shared/components/table/misc/translate-column-header';
import { BaseCloudImportExportComponent } from './base-cloud-import-export.component';

type SyncTableRowType = ClientBlaCompanyComposite & { statusText: string };

@Component({
  selector: 'app-sync-companies-between-byst-cloud',
  templateUrl: './sync-companies-between-byst-cloud.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BaseCloudImportExportComponent, useExisting: SyncCompaniesBetweenBystCloudComponent },
    getTranslationProvider('SyncCompaniesBetweenBystCloudComponent'),
  ],
})
export class SyncCompaniesBetweenBystCloudComponent extends BaseCloudImportExportComponent {
  @Output() updating: EventEmitter<void> = new EventEmitter<void>();
  @Output() updated: EventEmitter<void> = new EventEmitter<void>();

  companies$: Observable<SyncTableRowType[]>;
  selectedCompanies: ClientBlaCompanyComposite[] = [];
  columns$: Observable<TableColumn[]>;

  get hasSelectedCompanies() {
    return this.selectedCompanies.length > 0;
  }

  private columns = [
    { field: 'client.corporateIdentity', header: 'orgNumber' },
    { field: 'blaCompany.name', header: 'nameFromCompanyDatabase' },
    { field: 'client.customerNumber', header: 'customerNumber' },
    { field: 'client.responsibleUser', header: 'responsibleUser' },
    { field: 'blaCompany.publicKey', header: 'cloudDatabaseKey' },
    { field: 'statusText', header: 'infoAboutDatabase' },
  ];

  constructor(
    protected override cloudCompanyService: CloudCompanyService,
    private clientService: ClientService,
  ) {
    super(cloudCompanyService);
    this.companies$ = this.loadedDataSubject.asObservable();
    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns as TableColumn[]));
  }

  loadList(): void {
    const clientFilterFn = (client: Client) => !client.archived;

    this.getListData(clientFilterFn).subscribe({
      next: (response: ClientBlaCompanyComposite[]) => {
        this.loadedDataSubject.next(this.appendResponsibleUserPropToClients(this.appendStatusText(response)));
      },
      // eslint-disable-next-line no-console
      error: (err: unknown) => console.log('Error loading import list', err),
    });
  }

  execute(): void {
    if (!this.hasSelectedCompanies) {
      return;
    }

    this.updating.emit();

    const updates$ = this.selectedCompanies.map((companyComposite: ClientBlaCompanyComposite) => {
      const client = {
        ...toClientTypeTransformer.transform(companyComposite.client),
        cloudApiKey: companyComposite.blaCompany.publicKey,
      };
      const teamIds = companyComposite.client.teams.map((team) => team.id);

      return this.clientService.upsertClientTypeWithTeams(client, teamIds).pipe(
        map((c) => ({ success: true, data: toClientTransformer.transform(c) })),
        tap(() => this.store.dispatch(ClientActions.loadAllClients())),
        catchError(() => of({ success: false, data: companyComposite.client })),
      );
    });

    forkJoin(updates$).subscribe({
      next: (response) => {
        const hasSomeErrors = response.some((item) => !item.success);

        if (hasSomeErrors) {
          this.store.dispatch(
            ToastActions.showErrorMessage({
              summary: this.translate('anErrorOccured'),
              detail: this.translate('oneOrMoreClientNotUpdated'),
            }),
          );
        }
        const updatedClients = response.filter((item) => item.success).map((item) => item.data);
        this.selectedCompanies = [];
        this.updateClients(updatedClients);
        this.loadList();
        this.updated.emit();
      },
      complete: () => {
        this.updated.emit();
      },
    });
  }

  // Implementation of abstract parent method
  protected filterList(clients: Client[], blaCompanies: BlaCompany[]): ClientBlaCompanyComposite[] {
    return this.filterCloudCompanies(blaCompanies).reduce(
      (acc: ClientBlaCompanyComposite[], company: BlaCompany): ClientBlaCompanyComposite[] => {
        const client = clients.find((c) => !c.cloudApiKey && isIdentityMatch(c.corporateIdentity, company.orgNumber));

        if (client) {
          acc.push(new ClientBlaCompanyComposite(client, company));
        }

        return acc;
      },
      [],
    );
  }

  private filterCloudCompanies(blaCompanies: BlaCompany[]): BlaCompany[] {
    // exclude cloud companies without corp identity and public key
    return blaCompanies.filter((company: BlaCompany) => Boolean(company.orgNumber) && Boolean(company.publicKey));
  }

  private loadedDataSubject = new BehaviorSubject([]);

  private appendResponsibleUserPropToClients(cloudCompanies: ClientBlaCompanyComposite[]): ClientBlaCompanyComposite[] {
    return cloudCompanies.map((composite) => {
      const responsibleUser = composite.client.responsible?.name || '';
      return { ...composite, client: { ...composite.client, responsibleUser } };
    });
  }

  private appendStatusText(cloudCompanies: ClientBlaCompanyComposite[]): SyncTableRowType[] {
    const duplicatedOrgNumbers = this.getDuplicateOrgNumbers(cloudCompanies);

    return cloudCompanies.map((composite) => {
      const statusText = [];

      if (composite.blaCompany.hidden) {
        statusText.push(this.translate('hidden'));
      }

      if (duplicatedOrgNumbers.includes(composite.blaCompany.orgNumber?.replace(/-/g, ''))) {
        statusText.push(this.translate('duplicate'));
      }

      return { ...composite, statusText: statusText.join(', ') };
    });
  }

  private getDuplicateOrgNumbers(cloudCompanies: ClientBlaCompanyComposite[]): string[] {
    return cloudCompanies
      .map((composite) => composite.blaCompany)
      .filter((company) => Boolean(company.orgNumber))
      .filter((company, index, self) => self.findIndex((t) => t.orgNumber === company.orgNumber) !== index)
      .map((company) => company.orgNumber.replace(/-/g, ''));
  }
}
